import React from 'react';
import {DatePicker, Select} from 'antd';
import type {Dayjs} from 'dayjs';
import {useTranslation} from 'react-i18next';
import BatchInputFilterItem from '../../../delivery/components/BatchInputFilterItem';
import {PickupHubSelectFilter} from '../../../../components/filters/PickupHubSelectFilter';
import {getSmartLabelClassName} from '../../../../utils/labelWidthUtils';
import {
    useFilterContainerClassName,
    useFilterInputContainerClassName,
    useFilterLabelClassName
} from '../../../../utils/languageUtils';
import {PopReviewFilters, PopReviewStatus} from '../graphql/types/PopReviewType';
import type {ReasonOptionGroup} from '../hooks/usePopReviewOptions';

const {RangePicker} = DatePicker;

/**
 * 时间区间上限 1 个月，双向限制（选 start 后限 end；选 end 后限 start）。
 * info.from 由 antd RangePicker 提供。与 reviewTask（DSP 申诉审核）一致。
 */
const buildOneMonthDisabledDate = () => (current: Dayjs, info?: {from?: Dayjs}) => {
    if (!current || !info?.from) return false;
    return Math.abs(current.diff(info.from, 'day')) > 31;
};

interface PopReviewFilterProps {
    filters: PopReviewFilters;
    onChange: (filters: PopReviewFilters) => void;
    /** 不合格原因下拉选项；按分类分组展示，但仍是扁平多选（选中的是叶子 code） */
    reasonOptions: ReasonOptionGroup[];
    /** 揽收站点下拉（按当前 Hub 过滤；未选 Hub 时为空） */
    pickupSiteOptions: {value: string; label: string}[];
    /** 揽收司机下拉 */
    pickupDriverOptions: {value: string; label: string}[];
}

interface SingleSelectItemProps {
    label: string;
    value?: string;
    onChange: (value: string | undefined) => void;
    options: {label: string; value: string}[];
    spanClassName: string;
    placeholder: string;
    disabled?: boolean;
}

interface MultiSelectItemProps {
    label: string;
    value?: string[];
    onChange: (value: string[] | undefined) => void;
    /** 支持 antd 嵌套 options（分组）；分组仅为视觉分隔，选中的仍是叶子 value */
    options: ReasonOptionGroup[];
    spanClassName: string;
    placeholder: string;
}

/** 多选下拉筛选项；components/filters/ 下的既有组件均绑定了各自数据源，故就地实现 */
const MultiSelectFilterItem: React.FC<MultiSelectItemProps> = ({
    label,
    value,
    onChange,
    options,
    spanClassName,
    placeholder
}) => {
    const containerClassName = useFilterContainerClassName();
    const labelClassName = useFilterLabelClassName(spanClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    return (
        <div className={containerClassName}>
            <span className={labelClassName}>{label}</span>
            <div className={inputContainerClassName}>
                <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    size="middle"
                    className="w-full"
                    maxTagCount="responsive"
                    maxTagTextLength={10}
                    placeholder={placeholder}
                    value={value}
                    onChange={val => onChange(val && val.length > 0 ? val : undefined)}
                    options={options}
                    filterOption={(input, option) =>
                        String(option?.label ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                />
            </div>
        </div>
    );
};

/** 单选下拉筛选项；disabled 用于「站点须先选 Hub」这类前置依赖场景 */
const SingleSelectFilterItem: React.FC<SingleSelectItemProps> = ({
    label,
    value,
    onChange,
    options,
    spanClassName,
    placeholder,
    disabled
}) => {
    const containerClassName = useFilterContainerClassName();
    const labelClassName = useFilterLabelClassName(spanClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    return (
        <div className={containerClassName}>
            <span className={labelClassName}>{label}</span>
            <div className={inputContainerClassName}>
                <Select
                    showSearch
                    allowClear
                    size="middle"
                    className="w-full"
                    disabled={disabled}
                    placeholder={placeholder}
                    value={value || undefined}
                    onChange={val => onChange(val || undefined)}
                    options={options}
                    filterOption={(input, option) =>
                        String(option?.label ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                />
            </div>
        </div>
    );
};

const PopReviewFilter: React.FC<PopReviewFilterProps> = ({
    filters,
    onChange,
    reasonOptions,
    pickupSiteOptions,
    pickupDriverOptions
}) => {
    const {t} = useTranslation();

    const update = (patch: Partial<PopReviewFilters>) => onChange({...filters, ...patch});

    const labelAppointmentNumbers = t('popReview.filter.appointmentNumbers');
    const labelPickupSite = t('popReview.filter.pickupSite');
    const labelSubmittedAt = t('popReview.filter.submittedAt');
    const labelDriver = t('popReview.filter.driver');
    const labelReviewedAt = t('popReview.filter.reviewedAt');
    const labelPickupHub = t('popReview.filter.pickupHub');
    const labelStatus = t('popReview.filter.status');
    const labelDisqualifyReasons = t('popReview.filter.disqualifyReasons');

    // 同列 label 宽度统一：各列取本列全部 label 计算（须与下方 grid 的实际列位置一致）
    // 第 1 列：预约单号（独占整行，label 仍属本列）/ 集散中心 / 审核状态 / 提交时间
    const col1SpanClassName = getSmartLabelClassName([
        labelAppointmentNumbers,
        labelPickupHub,
        labelStatus,
        labelSubmittedAt
    ]);
    // 第 2 列：揽收站点 / 不合格原因 / 审核时间
    const col2SpanClassName = getSmartLabelClassName([
        labelPickupSite,
        labelDisqualifyReasons,
        labelReviewedAt
    ]);
    // 第 3 列：揽收司机
    const col3SpanClassName = getSmartLabelClassName([labelDriver]);

    // 时间项用裸 RangePicker（对齐 reviewTask），需自行套用 FilterItem 的样式结构
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();
    const labelCls1 = useFilterLabelClassName(col1SpanClassName);
    const labelCls2 = useFilterLabelClassName(col2SpanClassName);

    const statusOptions = [
        PopReviewStatus.PENDING_REVIEW,
        PopReviewStatus.APPROVED,
        PopReviewStatus.REJECTED,
        PopReviewStatus.CANCELLED
    ].map(status => ({
        label: t(`popReview.enum.status.${status}`) as string,
        value: status as string
    }));

    const allPlaceholder = t('popReview.filter.all') as string;

    return (
        <div className="mb-2 py-2">
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                {/* 预约单号为主查询项，独占整行（对齐 podReview 的运单号） */}
                <div className="min-w-0 col-span-3">
                    <BatchInputFilterItem
                        label={labelAppointmentNumbers}
                        value={filters.appointmentNumbers}
                        onChange={e => update({appointmentNumbers: e.target.value})}
                        spanClassName={col1SpanClassName}
                        placeholder={t('popReview.filter.appointmentNumbersPlaceholder') as string}
                        rows={2}
                    />
                </div>

                {/* 第 2 行：位置维度 */}
                <div className="min-w-0">
                    <PickupHubSelectFilter
                        label={labelPickupHub}
                        value={filters.pickupHubId}
                        onChange={value => update({pickupHubId: value})}
                        spanClassName={col1SpanClassName}
                        placeholder={allPlaceholder}
                    />
                </div>
                <div className="min-w-0">
                    {/* 站点从属于 Hub，未选 Hub 时无从查起，故禁用并提示先选 Hub */}
                    <SingleSelectFilterItem
                        label={labelPickupSite}
                        value={filters.pickupSiteId}
                        onChange={value => update({pickupSiteId: value})}
                        options={pickupSiteOptions}
                        spanClassName={col2SpanClassName}
                        disabled={!filters.pickupHubId}
                        placeholder={
                            filters.pickupHubId
                                ? allPlaceholder
                                : (t('popReview.filter.pickupSiteNeedHub') as string)
                        }
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelectFilterItem
                        label={labelDriver}
                        value={filters.driverId}
                        onChange={value => update({driverId: value})}
                        options={pickupDriverOptions}
                        spanClassName={col3SpanClassName}
                        placeholder={allPlaceholder}
                    />
                </div>

                {/* 第 3 行：审核维度 */}
                <div className="min-w-0">
                    <MultiSelectFilterItem
                        label={labelStatus}
                        value={filters.statuses}
                        onChange={value => update({statuses: value as PopReviewStatus[] | undefined})}
                        options={statusOptions}
                        spanClassName={col1SpanClassName}
                        placeholder={allPlaceholder}
                    />
                </div>
                <div className="min-w-0">
                    <MultiSelectFilterItem
                        label={labelDisqualifyReasons}
                        value={filters.disqualifyReasonCodes}
                        onChange={value => update({disqualifyReasonCodes: value})}
                        options={reasonOptions}
                        spanClassName={col2SpanClassName}
                        placeholder={allPlaceholder}
                    />
                </div>

                {/* 第 4 行：时间维度。组件与传参对齐 reviewTask（DSP 申诉审核）：
                    裸 RangePicker + dayjs 值 + 1 个月双向范围限制，只选日期不带时分秒 */}
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls1}>{labelSubmittedAt}</span>
                        <div className={inputContainerClassName}>
                            <RangePicker
                                size="middle"
                                className="w-full"
                                allowClear={false}
                                value={filters.submittedAt ?? null}
                                disabledDate={buildOneMonthDisabledDate()}
                                onChange={range => {
                                    // 必填字段：忽略清空动作，仅在区间完整时才更新；
                                    // 当日 [00:00, 23:59:59.999] 的规整统一在转入参处（usePopReviewQuery）做
                                    if (range && range[0] && range[1]) {
                                        update({submittedAt: [range[0], range[1]]});
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls2}>{labelReviewedAt}</span>
                        <div className={inputContainerClassName}>
                            <RangePicker
                                size="middle"
                                className="w-full"
                                value={filters.reviewedAt ?? null}
                                disabledDate={buildOneMonthDisabledDate()}
                                onChange={range => {
                                    if (range && range[0] && range[1]) {
                                        update({reviewedAt: [range[0], range[1]]});
                                    } else {
                                        update({reviewedAt: undefined});
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PopReviewFilter;
